import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const OrgContext = createContext(null);

const DEFAULT_BRAND = {
  slug: "",
  name: "",
  primary_color: "#0a0a0a",
  logo_url: null,
  plan: "trial",
  billing_status: "trial",
  is_founding_customer: false,
};

// Slug currently in the URL when navigating under /:slug/*. Superadmin
// uses this to switch branding per tenant they inspect; regular users
// stay on their own org regardless of the URL.
function useUrlSlug() {
  const { pathname } = useLocation();
  const first = pathname.split("/").filter(Boolean)[0];
  if (!first) return null;
  if (["admin", "login", "accept-invitation", "forgot-password", "reset-password", "r", "p"].includes(first)) return null;
  return first;
}

export function OrgProvider({ children }) {
  const { user } = useAuth();
  const urlSlug = useUrlSlug();
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      // Superadmin: OrgContext follows the URL slug (so navigating to
      // /agencia-x/utilizadores updates the sidebar branding to
      // "Agência X"). Falls back to platform-scoped null when browsing
      // /admin/* pages.
      if (user.role === "superadmin") {
        if (!urlSlug) {
          setOrg({ ...DEFAULT_BRAND, name: "PromoCrew — Superadmin", slug: "" });
          return;
        }
        const { data: list } = await api.get("/admin/organizations");
        const match = (list || []).find((o) => o.slug === urlSlug);
        setOrg(match ? { ...DEFAULT_BRAND, ...match } : { ...DEFAULT_BRAND, name: urlSlug, slug: urlSlug });
        return;
      }
      const { data } = await api.get("/organizations/me");
      setOrg({ ...DEFAULT_BRAND, ...data });
    } catch {
      setOrg(null);
    } finally {
      setLoading(false);
    }
  }, [user, urlSlug]);

  useEffect(() => {
    if (user) { refresh(); } else { setOrg(null); }
  }, [user, refresh]);

  // Apply primary_color as a CSS variable so the whole app picks it up
  useEffect(() => {
    if (!org?.primary_color) return;
    document.documentElement.style.setProperty("--brand-primary", org.primary_color);
  }, [org?.primary_color]);

  const value = useMemo(() => ({ org, loading, refresh }), [org, loading, refresh]);

  return (
    <OrgContext.Provider value={value}>
      {children}
    </OrgContext.Provider>
  );
}

export function useOrg() {
  const ctx = useContext(OrgContext);
  if (!ctx) throw new Error("useOrg must be used within OrgProvider");
  return ctx;
}
